import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Repeat, MessageCircle, Loader2, Clock, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { repeatOrder } from "@/lib/api";
import { formatDate, formatIDR, platformLabel } from "@/lib/format";

export default function RepeatOrder() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    repeatOrder.candidates().then(setRows).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const ready = rows.filter(r => r.priority === "ready");
  const soon = rows.filter(r => r.priority === "soon");
  const potentialRevenue = rows.reduce((s, r) => s + (r.total_spending || 0) * 0.6, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Repeat Order Engine</h1>
        <p className="text-sm text-gray-500 mt-1">Pelanggan yang siap re-order berdasarkan siklus produk. Kirim reminder sebelum kompetitor.</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-red-600 mb-2"><AlertCircle size={16} /><span className="text-xs font-bold uppercase tracking-wider">Siap Reorder</span></div>
            <div className="font-heading text-3xl font-bold text-gray-900">{ready.length}</div>
            <p className="text-xs text-gray-600 mt-1">Kirim reminder hari ini juga</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-amber-600 mb-2"><Clock size={16} /><span className="text-xs font-bold uppercase tracking-wider">Akan Habis</span></div>
            <div className="font-heading text-3xl font-bold text-gray-900">{soon.length}</div>
            <p className="text-xs text-gray-600 mt-1">Reminder dalam 7 hari ke depan</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-emerald-600 mb-2"><Repeat size={16} /><span className="text-xs font-bold uppercase tracking-wider">Potensi Revenue</span></div>
            <div className="font-heading text-2xl font-bold text-gray-900">{formatIDR(potentialRevenue)}</div>
            <p className="text-xs text-gray-600 mt-1">Estimasi jika 60% reorder</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-gray-200 overflow-hidden">
        {loading ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          : rows.length === 0 ? (
            <div className="py-16 text-center">
              <Repeat className="mx-auto text-gray-300" size={56} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada kandidat repeat order</h3>
              <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">Setelah customer belanja pertama, kami akan deteksi kapan mereka siap re-order berdasarkan siklus produk.</p>
              <Link to="/dashboard/products"><Button className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white">Atur Siklus Produk</Button></Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3">Pelanggan</th>
                    <th className="px-4 py-3">Produk Terakhir</th>
                    <th className="px-4 py-3">Marketplace</th>
                    <th className="px-4 py-3">Last Order</th>
                    <th className="px-4 py-3">Siklus</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{r.name}</div>
                        <div className="text-xs text-gray-500">{r.whatsapp_number}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.last_product_bought || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{platformLabel(r.source_platform)}</td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(r.last_order_date)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{r.reorder_cycle} hari</td>
                      <td className="px-4 py-3">
                        {r.priority === "ready" ? (
                          <Badge className="bg-red-50 text-red-700 border-red-200 border">Siap reorder</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">{r.days_until_reorder}hr lagi</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <a href={`https://wa.me/${(r.whatsapp_number || "").replace(/\D/g, "")}?text=Halo%20${r.name},%20kami%20lihat%20${r.last_product_bought || "produk"}%20yang%20kamu%20beli%20biasanya%20habis%20di%20minggu%20ini.%20Klaim%20cashback%20untuk%20reorder!`} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="outline" data-testid={`remind-${r.id}`}><MessageCircle size={14} className="mr-1" /> Kirim WA</Button>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}
